/* eslint-disable prettier/prettier */
import { useState, useEffect, useMemo } from 'react';
import {
	Activity,
	ActivityType,
	ScheduleViewMode,
	WeekRange,
} from '../types/schedule.types';
import { fetchActivities } from '../services/schedule.service';
import { toDateOnly } from '../utils/dateUtils';

export function useScheduleActivities(
	range: WeekRange | null,
	activeFilters: Set<ActivityType>,
	viewMode: ScheduleViewMode
) {
	const [allActivities, setAllActivities] = useState<Activity[]>([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const startKey = range ? range.start.getTime() : null;
	const endKey = range ? range.end.getTime() : null;

	useEffect(() => {
		if (startKey === null || endKey === null) return;
		let cancelled = false;

		setLoading(true);
		setError(null);

		fetchActivities({
			start: new Date(startKey),
			end: new Date(endKey),
		})
			.then(activities => {
				if (cancelled) return;
				setAllActivities(activities);
			})
			.catch((err: unknown) => {
				if (cancelled) return;
				setAllActivities([]);
				setError(
					err instanceof Error
						? err.message
						: 'Failed to load activities'
				);
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});

		return () => {
			cancelled = true;
		};
	}, [startKey, endKey, viewMode]);

	// Apply activity-type filter
	const filteredActivities = useMemo(() => {
		if (activeFilters.size === 0) return allActivities;
		return allActivities.filter(
			a =>
				a.activitytype &&
				activeFilters.has(a.activitytype as ActivityType)
		);
	}, [allActivities, activeFilters]);

	// Group by LOCAL date key, sorted by start time
	const activitiesByDate = useMemo(() => {
		const map = new Map<string, Activity[]>();
		for (const activity of filteredActivities) {
			const key = activity.startTime
				? toDateOnly(activity.startTime)
				: activity.date;
			if (!key) continue;
			if (!map.has(key)) map.set(key, []);
			map.get(key)!.push(activity);
		}

		map.forEach(acts => {
			acts.sort((a, b) => {
				if (!a.startTime && !b.startTime) return 0;
				if (!a.startTime) return 1;
				if (!b.startTime) return -1;
				return a.startTime.localeCompare(b.startTime);
			});
		});
		return map;
	}, [filteredActivities]);

	return { activitiesByDate, loading, error };
}
